const { getPoleOfInaccessibility } = require('../mapUtils');

/**
 * OffsetLabel
 * Creates a label placed near a polygon's center with a leader line.
 * - Uses a standard font size (stable across zooms)
 * - If the polygon is too small to fit the label, positions it outside
 * - Avoids overlaps with other labels by testing angles/distances
 * - Returns an L.LayerGroup containing a non-interactive L.Marker (label) and L.Polyline (leader)
 *
 * Usage: const layer = await OffsetLabel(polygonLayer, record)
 */

// --- Simple label collision manager (per-map) ---
const LabelManager = (function () {
  const maps = new Map(); // mapId -> { rects: Set<{id, rect}>, nextId }

  function getMapEntry(map) {
    const id = L.Util.stamp(map);
    if (!maps.has(id)) maps.set(id, { rects: new Map(), nextId: 1 });
    return { id, entry: maps.get(id) };
  }

  function register(map, layerGroup, rectPx) {
    const { id, entry } = getMapEntry(map);
    const rid = entry.nextId++;
    entry.rects.set(rid, rectPx);
    // cleanup on remove
    layerGroup.once('remove', () => {
      const e = maps.get(id);
      if (e) e.rects.delete(rid);
    });
  }

  function collides(map, rectPx) {
    const { entry } = getMapEntry(map);
    for (const r of entry.rects.values()) {
      if (rectsOverlap(r, rectPx)) return true;
    }
    return false;
  }

  function rectsOverlap(a, b) {
    return !(b.x > a.x + a.w ||
             b.x + b.w < a.x ||
             b.y > a.y + a.h ||
             b.y + b.h < a.y);
  }

  return { register, collides };
})();

// Rough text dimension estimation in pixels
function measureLabel(lines, fontPx) {
  const lineHeight = Math.round(fontPx * 1.2);
  const widths = lines.map(t => Math.ceil(t.length * fontPx * 0.6));
  const w = Math.max(...widths, 1);
  const h = lineHeight * lines.length;
  return { w, h, lineHeight };
}

function makeHTML(lines, fontPx) {
  const lineHeight = Math.round(fontPx * 1.2);
  const text = lines.map((t, i) =>
    `<div style="white-space:nowrap; line-height:${lineHeight}px;">${escapeHtml(t)}</div>`
  ).join('');
  return `<div style="font-size:${fontPx}px; font-family: sans-serif; background: rgba(255,255,255,0.0); pointer-events:none; text-align:center;">${text}</div>`;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Try to place the label. We search candidate (angle, distance) positions
 * and pick the first that doesn't collide with other labels.
 */
async function createOffsetLabel(layer, locationRecord, options = {}) {
  const map = layer._map;
  if (!map) throw new Error('OffsetLabel requires the layer to be on a map');

  const fontPx = options.fontPx || 14; // standard font size
  const leaderStyle = Object.assign({ color: '#000', weight: 1, opacity: 0.8 }, options.leaderStyle);
  const marginPx = options.marginPx || 6; // padding around label box for collision checks
  const startRadiusPx = options.startRadiusPx || 5; // initial offset from center in pixels
  const maxRadiusPx = options.maxRadiusPx || 20; // how far we try before giving up

  // Determine center (prefer pole of inaccessibility)
  let centerLL;
  try {
    const pole = await getPoleOfInaccessibility(layer, 0.00001);
    if (Array.isArray(pole)) centerLL = L.latLng(pole[1], pole[0]);
  } catch (_) {}
  if (!centerLL) centerLL = layer.getBounds().getCenter();

  // Determine if the label could fit entirely inside polygon bounds (rough check)
  const lines = locationRecord.roomNumber
    ? [String(locationRecord.roomNumber), String(locationRecord.name)]
    : [String(locationRecord.name)];
  const dims = measureLabel(lines, fontPx);

  const b = layer.getBounds();
  const swPx = map.latLngToLayerPoint(b.getSouthWest());
  const nePx = map.latLngToLayerPoint(b.getNorthEast());
  const polyW = Math.abs(nePx.x - swPx.x);
  const polyH = Math.abs(nePx.y - swPx.y);
  const fitsInsideBounds = (polyW >= dims.w + marginPx * 2) && (polyH >= dims.h + marginPx * 2);

  // Build candidate positions around center (spiral-ish by increasing radius, multiple angles)
  const centerPx = map.latLngToLayerPoint(centerLL);
  const angles = [];
  const steps = options.angleSteps || 12; // 30° steps by default
  for (let i = 0; i < steps; i++) angles.push((i / steps) * Math.PI * 2);
  // Shuffle start angle by polygon index-ish to reduce alignment; fall back to 0
  const jitter = (centerPx.x + centerPx.y) % steps;
  const angleOrder = angles.map((a, i) => angles[(i + jitter) % steps]);

  const radii = [];
  for (let r = startRadiusPx; r <= maxRadiusPx; r += 12) radii.push(r);

  // If it fits, try radius 0 as well (center placement)
  if (fitsInsideBounds) radii.unshift(0);

  let chosen = null;
  for (const r of radii) {
    for (const a of angleOrder) {
      const cx = centerPx.x + Math.cos(a) * r;
      const cy = centerPx.y + Math.sin(a) * r;
      const rect = { x: Math.round(cx - dims.w / 2) - marginPx,
                     y: Math.round(cy - dims.h / 2) - marginPx,
                     w: dims.w + marginPx * 2,
                     h: dims.h + marginPx * 2 };
      if (!LabelManager.collides(map, rect)) {
        chosen = { rect, a, r, boxCenterPx: L.point(cx, cy) };
        break;
      }
    }
    if (chosen) break;
  }

  // If all positions collide, just place at last tested position
  if (!chosen) {
    const r = maxRadiusPx, a = 0;
    const cx = centerPx.x + Math.cos(a) * r;
    const cy = centerPx.y + Math.sin(a) * r;
    chosen = { rect: { x: cx - dims.w / 2, y: cy - dims.h / 2, w: dims.w, h: dims.h }, a, r, boxCenterPx: L.point(cx, cy) };
  }

  // Convert chosen pixel center to LatLng
  const labelCenterLL = map.layerPointToLatLng(chosen.boxCenterPx);

  // Leader line from polygon center to closest point on label box (use center-to-center for simplicity)
  const leader = L.polyline([centerLL, labelCenterLL], Object.assign({ interactive: false, bubblingMouseEvents: true }, leaderStyle));

  // Build an SVG overlay for the label so it scales with the map like textMarker
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('viewBox', `0 0 ${dims.w} ${dims.h}`);
  svg.style.pointerEvents = 'none';

  const bg = document.createElementNS(svg.namespaceURI, 'rect');
  bg.setAttribute('width', String(dims.w));
  bg.setAttribute('height', String(dims.h));
  bg.setAttribute('fill', 'none');
  svg.appendChild(bg);

  // Add text lines centered
  const { lineHeight } = dims;
  lines.forEach((txt, i) => {
    const t = document.createElementNS(svg.namespaceURI, 'text');
    t.setAttribute('x', String(dims.w / 2));
    t.setAttribute('y', String((i + 0.5) * lineHeight));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('dominant-baseline', 'middle');
    t.setAttribute('style', `font-size:${fontPx}px;fill:#000;pointer-events:none`);
    t.textContent = txt;
    svg.appendChild(t);
  });

  // Compute overlay bounds from pixel rect so size is appropriate at current zoom
  const halfW = Math.round(dims.w / 2);
  const halfH = Math.round(dims.h / 2);
  const topLeftPx = L.point(chosen.boxCenterPx.x - halfW, chosen.boxCenterPx.y - halfH);
  const botRightPx = L.point(chosen.boxCenterPx.x + halfW, chosen.boxCenterPx.y + halfH);
  const topLeftLL = map.layerPointToLatLng(topLeftPx);
  const botRightLL = map.layerPointToLatLng(botRightPx);

  const labelOverlay = L.svgOverlay(svg, [
    [topLeftLL.lat, topLeftLL.lng],
    [botRightLL.lat, botRightLL.lng]
  ], { interactive: false, bubblingMouseEvents: true });

  const group = L.layerGroup([leader, labelOverlay]);

  // Register rect for collision avoidance (using absolute pixel rect w/h)
  LabelManager.register(map, group, { x: chosen.rect.x, y: chosen.rect.y, w: chosen.rect.w, h: chosen.rect.h });

  return group;
}

module.exports = createOffsetLabel;